import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { Copy, Mail, Phone, CalendarDays } from "lucide-react";
import toast from "react-hot-toast";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/clipboard";
import { formatDate } from "@/utils/dateUtils";

const GAP = 8;
const EDGE = 8;

// Compliance data is used to contact real people, so a missing phone shows an
// em dash and disables its actions rather than falling back to a
// plausible-looking placeholder.
const DASH = "—";

const ContactRow = ({ icon: Icon, label, value, href, copyLabel }) => {
  const has = Boolean(value);

  const handleCopy = async (e) => {
    // The popover opens from inside a table row / page header that has its
    // own click handler; copying must not also navigate.
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard(value);
    if (ok) toast.success(`${copyLabel} copied`);
    else toast.error(`Could not copy ${copyLabel.toLowerCase()}`);
  };

  return (
    <div className="flex items-center gap-2 min-w-0">
      <Icon size={13} className="shrink-0 text-text-secondary" />
      <div className="min-w-0 flex-1">
        <p className="text-[11px] font-medium text-text-secondary">{label}</p>
        {has && href ? (
          <a href={href} onClick={(e) => e.stopPropagation()} className="block truncate text-xs text-text-primary hover:text-accent-text hover:underline" title={value}>
            {value}
          </a>
        ) : (
          <p className="truncate text-xs text-text-primary" title={has ? value : undefined}>
            {has ? value : DASH}
          </p>
        )}
      </div>
      {copyLabel && (
        <Button
          type="button"
          disabled={!has}
          onClick={handleCopy}
          title={has ? `Copy ${copyLabel.toLowerCase()}` : `No ${copyLabel.toLowerCase()} on file`}
          className="h-7 w-7 shrink-0 !p-0 rounded-lg flex items-center justify-center text-text-secondary hover:text-text-primary hover:bg-background-icon disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Copy size={12} />
        </Button>
      )}
    </div>
  );
};

/**
 * Candidate contact quick-card.
 *
 * Wraps a trigger (usually the candidate email) and shows contact details on
 * hover/focus: email and phone as mailto:/tel: links so the mail client or
 * dialler opens on click, each with its own copy button, plus the date the
 * candidate was sent to compliance.
 *
 * Positioned the same way as shared/CustomTooltip — portalled to document.body
 * and fixed — so it is never clipped by a table overflow-x-auto container or a
 * card boundary. Unlike the tooltip it is interactive, so it stays open while
 * the pointer is inside the bubble.
 *
 * `candidatePhone` is not in the compliance API responses yet
 * (BACKEND_REQUIREMENTS #5); that row renders an em dash until it is.
 */
const CandidateContactPopover = ({ candidate, children, className = "" }) => {
  const [open, setOpen] = useState(false);
  const [style, setStyle] = useState({});
  const wrapperRef = useRef(null);
  const bubbleRef = useRef(null);
  const closeTimer = useRef(null);

  const email = candidate?.candidateEmail || "";
  const phone = candidate?.candidatePhone || "";
  const sentAt = candidate?.sentAt || candidate?.createdAt || "";

  // Keep the bubble open while the pointer crosses the gap between trigger
  // and card, otherwise the copy buttons are unreachable.
  const cancelClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  useEffect(() => () => cancelClose(), []);

  useEffect(() => {
    if (!open) return;
    const trigger = wrapperRef.current?.getBoundingClientRect();
    const bubble = bubbleRef.current?.getBoundingClientRect();
    if (!trigger || !bubble) return;

    const below = trigger.top - GAP - bubble.height < EDGE;
    const left = Math.min(Math.max(trigger.left, EDGE), window.innerWidth - bubble.width - EDGE);

    setStyle({
      position: "fixed",
      left,
      top: below ? trigger.bottom + GAP : trigger.top - GAP - bubble.height,
      zIndex: 9999,
    });
  }, [open]);

  // A fixed bubble does not travel with the row beneath it.
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setOpen(false);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  if (!candidate) return children;

  return (
    <div
      ref={wrapperRef}
      className={cn("relative inline-flex max-w-full", className)}
      onMouseEnter={() => {
        cancelClose();
        setOpen(true);
      }}
      onMouseLeave={scheduleClose}
      onFocus={() => setOpen(true)}
      onBlur={scheduleClose}
    >
      {children}

      {open &&
        createPortal(
          <div
            ref={bubbleRef}
            role="dialog"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onClick={(e) => e.stopPropagation()}
            className="w-[260px] rounded-xl border border-border-primary bg-background-secondary p-3 shadow-xl animate-in fade-in duration-150"
            style={{ position: "fixed", top: -9999, left: -9999, zIndex: 9999, ...style }}
          >
            <p className="mb-2 truncate text-[13px] font-semibold text-text-primary">{candidate.candidateName || DASH}</p>

            <div className="flex flex-col gap-2">
              <ContactRow icon={Mail} label="Email" value={email} href={email ? `mailto:${email}` : undefined} copyLabel="Email" />
              <ContactRow icon={Phone} label="Phone" value={phone} href={phone ? `tel:${phone.replace(/\s+/g, "")}` : undefined} copyLabel="Phone" />
              <ContactRow icon={CalendarDays} label="Sent to Compliance" value={sentAt ? formatDate(sentAt) : ""} />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default CandidateContactPopover;
